import React, { useState, useEffect, useCallback } from 'react';
import { HeaderLeft } from '../../components/layout/HeaderSlot';
import { FiHome, FiChevronRight, FiGrid, FiTag, FiLink, FiPackage, FiShield, FiPercent, FiActivity } from 'react-icons/fi';
import DbViewer from '../db-viewer/components/DbViewer';
import PlanServiceMatrix from './PlanServiceMatrix';
import RelationshipWorkbench from './RelationshipWorkbench';
import { confirmDiscardChanges, setFormDirty } from '../../lib/unsavedChanges';
import '../bookkeeping/components/BookkeepingPage.css';
import './RolesPermissionsPage.css';

// =============================================
// Roles & Permissions — management console.
// Each tab embeds the generic DbViewer scoped (locked) to one table, giving
// full add / edit / delete that persists to the DB (localStorage or API).
// =============================================

type RPTab = 'workbench' | 'services' | 'plans' | 'plan-access' | 'client-packages' | 'promos' | 'permissions';

const TABS: { key: RPTab; label: string; table: string; icon: React.ReactNode; hint: string }[] = [
  { key: 'workbench',       label: 'Workbench',        table: '',                        icon: <FiActivity size={15} />, hint: 'One screen, three lenses (Service / Plan / Role). Pick a subject, then wire its relationships — changes write straight to the link tables. Start here; the tabs to the right are the raw editors.' },
  { key: 'services',        label: 'Services',         table: 'site_services',           icon: <FiGrid size={15} />,    hint: 'Master catalog of services & sub-modules. Edit, add, delete, or re-parent (move under another service via parent_service_id).' },
  { key: 'plans',           label: 'Plans',            table: 'site_service_plans',      icon: <FiTag size={15} />,     hint: 'Pricing plans — prices, limits (max docs/clients/invoices, retention years), and feature flags.' },
  { key: 'plan-access',     label: 'Plan ↔ Services',  table: 'site_service_plan_access',icon: <FiLink size={15} />,    hint: 'Visual matrix: the service tree (expand parents for sub-modules) × each plan. Click a cell to cycle its access level (none → read → limited → full).' },
  { key: 'client-packages', label: 'Client Packages',  table: 'site_client_packages',    icon: <FiPackage size={15} />, hint: 'Per-client bookkeeping packages (BK / payroll / schedules / tax) with prices and limits.' },
  { key: 'promos',          label: 'Promos',           table: 'site_service_promos',     icon: <FiPercent size={15} />, hint: 'Promotional discounts / coupons — % or fixed amount off a plan, client package, or service, with redemption limits and dates.' },
  { key: 'permissions',     label: 'Permissions',      table: 'user_rights',             icon: <FiShield size={15} />,  hint: 'Which user-type may use which service (Yes/No). Drives role-based nav visibility.' },
];

function resolveTabFromHash(): RPTab {
  const hash = window.location.hash.replace('#', '');
  return TABS.some(t => t.key === hash) ? (hash as RPTab) : 'workbench';
}

const RolesPermissionsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<RPTab>(resolveTabFromHash());
  // Bumped to remount (reset to landing) when the active tab is re-clicked.
  const [tabResetKey, setTabResetKey] = useState(0);

  useEffect(() => {
    const newHash = '#' + activeTab;
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
    }
  }, [activeTab]);

  useEffect(() => {
    const onHashChange = () => setActiveTab(resolveTabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const switchTab = useCallback(async (tab: RPTab) => {
    if (!(await confirmDiscardChanges())) return;
    setFormDirty(false);
    // Re-clicking the active tab resets it to its landing (remount); otherwise switch.
    if (tab === activeTab) setTabResetKey(k => k + 1);
    else setActiveTab(tab);
  }, [activeTab]);

  const active = TABS.find(t => t.key === activeTab)!;

  return (
    <div className="rp-page">
      <HeaderLeft>
<nav className="bk-breadcrumbs" aria-label="Breadcrumb">
        <span className="bk-breadcrumb-item bk-breadcrumb-link" onClick={async () => { if (!(await confirmDiscardChanges())) return; setFormDirty(false); window.location.href = '/admin'; }}>
          <FiHome size={13} /> Home
        </span>
        <FiChevronRight size={12} className="bk-breadcrumb-sep" />
        <span className="bk-breadcrumb-item bk-breadcrumb-current">Roles &amp; Permissions</span>
        <FiChevronRight size={12} className="bk-breadcrumb-sep" />
        <span className="bk-breadcrumb-item bk-breadcrumb-current">{active.label}</span>
      </nav>
</HeaderLeft>

      <div className="rp-header">
        <h2>Roles &amp; Permissions</h2>
        <p>Manage services, plans, packages, and per-type permissions. Changes here write straight to the database tables.</p>
      </div>

      <div className="rp-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`rp-tab ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => switchTab(t.key)}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="rp-hint">{active.hint}</div>

      <div className="rp-content">
        <React.Fragment key={`${activeTab}-${tabResetKey}`}>
          {activeTab === 'workbench' ? (
            /* Relationship Workbench — the visual landing view (Service / Plan / Role lenses) */
            <RelationshipWorkbench />
          ) : activeTab === 'plan-access' ? (
            /* Visual services↔plans matrix (replaces the flat table for this tab) */
            <PlanServiceMatrix />
          ) : (
            /* key forces a fresh DbViewer per tab so it locks onto the right table */
            <DbViewer key={active.table} initialTable={active.table} lockTable />
          )}
        </React.Fragment>
      </div>
    </div>
  );
};

export default RolesPermissionsPage;
